import { ChangeDetectionStrategy, Component, computed, contentChild, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { CadsFieldComponent } from '../cads-field/cads-field.component';
import { CadsFieldCaptionSlotDirective } from '../cads-field/cads-field-caption-slot.directive';
import type { CadsFieldConfig } from '../cads-field/cads-field.config';
import { nextCadsFormKitId } from '../internal/cads-form-kit-ids';

import type { CadsTextareaConfig } from './cads-textarea.config';

/**
 * Reactive-forms native `<textarea>` for multi-line free text. Implements {@link ControlValueAccessor}; visual chrome
 * comes from the embedded {@link CadsFieldComponent}. Optional character counter renders only when `maxLength` is set
 * and `showCounter` is **true**.
 */
@Component({
    selector: 'cads-textarea',
    imports: [CadsFieldComponent],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CadsTextareaComponent),
            multi: true,
        },
    ],
    templateUrl: './cads-textarea.component.html',
    styleUrl: './cads-textarea.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'cads-textarea-host',
    }
})
export class CadsTextareaComponent implements ControlValueAccessor {
  readonly config = input.required<CadsTextareaConfig>();

  private readonly _fallbackInputId = nextCadsFormKitId('cads-textarea');

  protected readonly _value = signal<string>('');
  protected readonly _focused = signal(false);
  protected readonly _controlDisabled = signal(false);

  protected readonly _inputId = computed(() => {
    const fromConfig = this.config().inputId?.trim();
    return fromConfig || this._fallbackInputId;
  });

  protected readonly _disabled = computed(() => this._controlDisabled() || this.config().disabled === true);

  protected readonly _hasError = computed(() => (this.config().errorMessage?.trim() ?? '').length > 0);

  private readonly _captionSlot = contentChild(CadsFieldCaptionSlotDirective);

  protected readonly _hasProjectedCaption = computed(() => this._captionSlot() !== undefined);

  protected readonly _captionId = computed(() => `${this._inputId()}-caption`);

  protected readonly _showCaption = computed(() => {
    if (this._hasError()) {
      return true;
    }
    if (this._hasProjectedCaption()) {
      return true;
    }
    return (this.config().helperText?.trim() ?? '').length > 0;
  });

  protected readonly _showCounter = computed(
    () => this.config().showCounter === true && typeof this.config().maxLength === 'number',
  );

  protected readonly _fieldConfig = computed((): CadsFieldConfig => ({
    label: this.config().label,
    controlId: this._inputId(),
    helperText: this.config().helperText,
    errorMessage: this.config().errorMessage,
    required: this.config().required === true,
  }));

  private _onChange: (value: string) => void = () => undefined;
  private _onTouched: () => void = () => undefined;

  writeValue(value: string | null | undefined): void {
    this._value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._controlDisabled.set(isDisabled);
  }

  protected _onInput(event: Event): void {
    const next = (event.target as HTMLTextAreaElement).value;
    this._value.set(next);
    this._onChange(next);
  }

  protected _onFocus(): void {
    this._focused.set(true);
  }

  protected _onBlur(): void {
    this._focused.set(false);
    this._onTouched();
  }
}
